/**
 * Detailed AI Solutions catalog for Quantum Star Marketing.
 *
 * Each entry expands on a feature shown in the AISolutions section grid,
 * with detailed working examples for every recent client engagement.
 */
const AI_SOLUTIONS = [
  {
    key: "ai-lead-qualification",
    eyebrow: "AI Operating System",
    name: "AI Lead Qualification",
    tagline: "Score, route and nurture leads in real time.",
    overview:
      "Most agencies optimise toward form-fills. We optimise toward closed revenue. Our AI scoring layer ingests behavioural signals (page depth, scroll, time-on-site), declared intent (form answers, chat replies), and firmographic data (company, role, industry) to grade every lead inside 8 seconds — then routes hot leads to your sales team's mobile in real time.",
    highlights: [
      "Real-time scoring model (0–100) trained on your historic closed deals",
      "Behavioural + firmographic + intent signals fused into one rank",
      "Auto-routing to sales rep mobile / WhatsApp inside 60 seconds",
      "Cold leads dropped into automated nurture sequences",
      "Live dashboard: leads in, leads scored, leads routed, leads won",
      "Monthly model retraining on your latest closed-won / lost data",
      "CRM-agnostic: HubSpot, Pipedrive, Zoho, Salesforce or custom",
    ],
    highlightsLabel: "What's inside the system",
    phases: [
      { day: "Week 1", body: "Audit current lead flow + sample 200 closed deals to train baseline model." },
      { day: "Week 2–3", body: "Wire scoring into your forms, chat and CRM. Live shadow-mode testing." },
      { day: "Week 4+", body: "Go-live + weekly tuning. By month 2, sales talks only to top-30% of leads." },
    ],
    phasesLabel: "30-day deployment",
    outcomes: [
      "Sales time spent on bad leads down 60–80%",
      "Lead-to-meeting conversion up 35–60%",
      "Speed-to-lead under 60 seconds (vs industry avg 47 hours)",
      "Sales cycle shortened by 20–35%",
    ],
    outcomesLabel: "Typical 90-day outcomes",
    examples: [
      "Dubai real estate brokerage with 40 agents — scored 12,400 leads in their first month with us, routed only the top 1,800 to agent WhatsApp. Closed deals per agent rose from 1.8 to 3.4 monthly; speed-to-lead fell from 4 hours to 47 seconds.",
      "B2B SaaS targeting MENA fleet operators — model trained on 200 prior closed-won deals identified 'fleet size 50+' plus 'pricing page visit' as the highest-converting signal pair. Sales-rep time on bad leads cut 71%; pipeline up 238% in 9 months.",
      "Multi-location UAE dental group — converted lead scores directly into next-day appointment slots based on perceived urgency. Average days-to-first-visit dropped from 11 days to 3 days; treatment-conversion of consultations up 28%.",
      "GCC EdTech admissions team — auto-disqualified ineligible applicants based on visa status + degree fields before they reached the human advisor queue. Advisor capacity freed up 64%; enrolment conversion of advised applicants doubled.",
    ],
  },
  {
    key: "predictive-analytics",
    eyebrow: "AI Operating System",
    name: "Predictive Analytics",
    tagline: "Forecast LTV, churn and revenue 60 days ahead.",
    overview:
      "Stop reporting on what already happened. Our predictive layer fuses your CRM, ad platforms, and product data into a single forecasting engine. Every Monday, your leadership team sees a 60-day forward view: projected revenue, projected churn, which cohorts will pay back, and which campaigns are about to underperform — with confidence intervals you can defend in a board meeting.",
    highlights: [
      "60-day revenue forecast with confidence bands",
      "Per-customer LTV prediction (90 / 365 / lifetime)",
      "Churn-risk scoring with intervention triggers",
      "Cohort payback analysis by acquisition channel",
      "Campaign-level revenue forecasts (before you spend)",
      "Weekly anomaly alerts pushed to Slack / email",
      "Board-grade dashboard you can present in 5 minutes",
    ],
    highlightsLabel: "What's inside the system",
    phases: [
      { day: "Week 1–2", body: "Connect CRM, GA4, ad platforms, payment data into a unified warehouse." },
      { day: "Week 3–4", body: "Build + validate forecasting models against last 18 months of data." },
      { day: "Week 5+", body: "Live forecasts pushed weekly to your leadership team." },
    ],
    phasesLabel: "Deployment timeline",
    outcomes: [
      "Revenue forecast accuracy within ±8% at 60 days out",
      "Churn-risk customers retained at 25–45% rate vs 0% baseline",
      "Budget reallocation decisions backed by data, not vibes",
      "Investor + board reporting cycle shortened from days to minutes",
    ],
    outcomesLabel: "Typical outcomes",
    examples: [
      "UAE-based DTC fragrance brand — model flagged a forecasted TikTok CPM spike 12 days ahead of Ramadan. Reallocated AED 1.4M of planned spend into Meta + creator-led campaigns; full quarter ROAS held at 5.8x vs forecast of 3.4x had they not moved.",
      "MENA SaaS Series-A raise — used 24-month forward revenue forecast inside the investor data room. Lead investor cited forecast credibility as a closing factor; round closed at AED 28M against an initial AED 18M target.",
      "GCC subscription coffee brand — churn model flagged an 18% at-risk cohort 45 days early. Triggered a personalised AED 30 credit offer; saved 64% of that cohort, recovering AED 320K in annualised subscription revenue.",
      "Premium F&B group across UAE — demand-forecast model reduced Q4 overstock waste from 14% to 4.6% of inventory value. Translated to AED 480K in saved cost-of-goods over the festive trading window.",
    ],
  },
  {
    key: "ai-sales-funnels",
    eyebrow: "AI Operating System",
    name: "AI Sales Funnels",
    tagline: "Conversational journeys that adapt to each visitor.",
    overview:
      "Static landing pages convert at 2-3%. Our conversational AI funnels convert at 8-15% — by adapting the headline, offer, social proof and CTA to who's actually viewing the page. The AI reads visitor intent in real time (traffic source, scroll behaviour, returning visitor, declared answers) and reshapes the funnel on the fly.",
    highlights: [
      "Conversational landing page (typeform-style + AI brain)",
      "Dynamic copy that adapts to visitor intent in <500ms",
      "Personalised offers per segment (price, bonus, urgency)",
      "Smart objection handling baked into the flow",
      "Multi-step qualifying logic without losing momentum",
      "Auto-booking into sales calendar with pre-call brief sent to rep",
      "Bilingual EN/AR with native nuance",
    ],
    highlightsLabel: "What's inside the system",
    phases: [
      { day: "Week 1", body: "Map current funnel; identify 3 highest-ICP visitor segments." },
      { day: "Week 2–3", body: "Build AI-adaptive page, copy variants, and CRM hand-off." },
      { day: "Week 4+", body: "Go-live with A/B test vs old funnel. Iterate weekly on data." },
    ],
    phasesLabel: "Build timeline",
    outcomes: [
      "Landing-to-booked-call conversion 3–5x baseline",
      "Cost per booked call down 40–60%",
      "Sales reps walk into calls with full pre-call brief",
      "Compounding learnings — funnel gets smarter every week",
    ],
    outcomesLabel: "Typical outcomes",
    examples: [
      "UAE high-ticket coaching offer at AED 35K — built an adaptive funnel that surfaces a different case-study and pricing-anchor depending on declared revenue range. Booked-call rate from 4.1% → 13.4%; close-rate on calls rose from 18% to 34%.",
      "Dubai real-estate off-plan launch — qualifying logic captures budget + timeline + bedroom count before showing the unit selection. Qualified-viewing requests up 220%; deposit-conversion-of-viewing doubled because buyers self-qualified pre-call.",
      "B2B MENA consultancy — replaced 4 separate service landing pages with one adaptive funnel keyed off industry + role. Cost per qualified strategy call dropped 58%; sales-team pre-call brief now arrives 2 minutes before every call.",
      "Aesthetics clinic chain across 6 UAE locations — funnel asks treatment goal + budget range + preferred location, then offers an in-clinic consultation slot. Consultation bookings up 180% at the same ad spend; treatment-conversion of consultations rose 36%.",
    ],
  },
  {
    key: "automated-reporting",
    eyebrow: "AI Operating System",
    name: "Automated Reporting",
    tagline: "Boardroom-ready dashboards. No spreadsheets.",
    overview:
      "Your team should not be spending 6 hours every Monday building a status deck. We replace status meetings with always-on, board-grade dashboards that pull live data from every channel — paid, organic, CRM, payments, retention — and tell the story in plain English, automatically.",
    highlights: [
      "Single source of truth across all marketing + revenue channels",
      "Auto-generated weekly executive summary (written by AI, reviewed by analyst)",
      "Drill-down from KPIs to campaign-level diagnostics in 2 clicks",
      "Slack / WhatsApp / email push of anomalies",
      "Custom views per role (CEO, CMO, head of growth, sales)",
      "Investor-ready monthly board pack auto-assembled",
      "Bilingual EN/AR labels for GCC leadership teams",
    ],
    highlightsLabel: "What's inside the system",
    phases: [
      { day: "Week 1", body: "Audit current reporting; map every data source." },
      { day: "Week 2–3", body: "Build warehouse + dashboards + anomaly detection." },
      { day: "Week 4+", body: "Live dashboards replace all status meetings + decks." },
    ],
    phasesLabel: "Build timeline",
    outcomes: [
      "Reporting time saved: 12–20 hours per week across team",
      "Decision speed 3-5x faster — data is already there",
      "Investor / board updates shipped in minutes, not days",
      "Anomalies surfaced within hours, not at month-end",
    ],
    outcomesLabel: "Typical outcomes",
    examples: [
      "MENA HRTech at Series A — replaced 4 separate weekly status calls (paid, lifecycle, CS, sales) with one live dashboard. Founders save ~16 hours per week of meeting time; weekly board update now runs as a 9-minute Loom over the dashboard.",
      "GCC fintech (DIFC-licensed) — auto-assembled monthly board pack for the DIFC regulator covering AML, transaction velocity, and risk metrics. Pack production time fell from 3 working days to 22 minutes; zero late submissions in 14 months.",
      "Dubai real estate brokerage with 40 agents — built a listing-to-revenue dashboard that surfaces per-agent close rate, average days-to-close, and lead-quality scores. Underperforming agents identified weeks earlier; team-wide GCI per head rose 31%.",
      "Multi-location UAE healthcare network — clinic-level P&L dashboard updates every 6 hours from the practice-management system. Two underperforming clinics turned around within 90 days based on cost-per-acquisition data the network previously couldn't see.",
    ],
  },
  {
    key: "ai-customer-support",
    eyebrow: "AI Operating System",
    name: "AI Customer Support",
    tagline: "Tier-1 support agents, trained on your brand. 24/7.",
    overview:
      "Most support tickets are the same 15 questions answered badly. Our AI support agents are trained on your knowledge base, brand voice, and historical tickets — handling 60–80% of inbound across web, WhatsApp, Instagram and email, with seamless escalation to humans for anything legally sensitive or complex.",
    highlights: [
      "Multi-channel: web widget, WhatsApp Business, Instagram DM, email",
      "Trained on your KB, policies, FAQs and historical transcripts",
      "Bilingual EN/AR with proper Gulf-Arabic dialect handling",
      "Human-escalation logic baked in (sentiment, complexity, legal triggers)",
      "Order-status / booking lookups via API hand-off",
      "Tone of voice matches your brand (warm, premium, technical — your call)",
      "Weekly transcript review + prompt tuning",
    ],
    highlightsLabel: "What's inside the system",
    phases: [
      { day: "Week 1", body: "KB ingestion + brand-voice training + escalation rules." },
      { day: "Week 2", body: "Channel integration + sandbox QA across 500 mock tickets." },
      { day: "Week 3+", body: "Live; weekly transcript review with your support lead." },
    ],
    phasesLabel: "Deployment timeline",
    outcomes: [
      "60–80% of tickets auto-resolved without human touch",
      "Average response time under 8 seconds (vs 4–24 hour baseline)",
      "Support cost down 40–70%",
      "CSAT scores up 15–35% (faster + more consistent answers)",
    ],
    outcomesLabel: "Typical outcomes",
    examples: [
      "UAE multi-location dental group with 28 clinics — WhatsApp agent handles bookings, insurance pre-checks, and procedure FAQs in Arabic + English. Reception workload fell 64%; weekend appointment bookings grew 184% because the agent never sleeps.",
      "DTC e-commerce on Shopify across GCC — WhatsApp agent handles order-status, returns, and product Q&A. 73% of post-purchase tickets resolved without a human; CSAT score climbed from 4.1 to 4.7 within 90 days.",
      "MENA EdTech admissions team — pre-qualified 4,200+ applicants in Q2 in conversational format. Advisors received only top-scoring 18% in their queue; enrolment conversion rate of advised applicants doubled.",
      "B2B SaaS at Series B — agent handles tier-1 product questions on the pricing page and in-app. Engineering team interruptions for support questions fell 81%; product-onboarding completion rate up 24%.",
    ],
  },
  {
    key: "intelligent-ad-optimization",
    eyebrow: "AI Operating System",
    name: "Intelligent Ad Optimization",
    tagline: "Bid, creative and audience tuning across every channel.",
    overview:
      "Manual ad management caps out around AED 200K/month spend. Beyond that, you need an AI layer that reads performance hourly, reallocates budget across Meta / Google / TikTok / YouTube based on incremental ROAS, kills losing creatives, and scales winners — without waiting for a Monday meeting.",
    highlights: [
      "Hourly bid + budget reallocation across all major ad platforms",
      "Creative fatigue detection — pause + rotate before CPMs spike",
      "Incremental ROAS measurement (not just last-click)",
      "Audience expansion + lookalike refresh on a 14-day cadence",
      "Anomaly alerts (sudden CPM jumps, tracking breaks)",
      "Cross-channel budget shifting based on marginal ROAS",
      "Weekly analyst review on top of AI decisions",
    ],
    highlightsLabel: "What's inside the system",
    phases: [
      { day: "Week 1–2", body: "Audit accounts; install incrementality + conversion-API tracking." },
      { day: "Week 3–4", body: "Turn on AI bid + budget layer in shadow mode." },
      { day: "Week 5+", body: "Full automation with weekly analyst oversight." },
    ],
    phasesLabel: "Deployment timeline",
    outcomes: [
      "Blended ROAS up 25–60% at same spend",
      "Wasted spend (under-performers) cut 30–50%",
      "Creative testing velocity 3–5x faster",
      "Ad operations time saved: 15–25 hours/week",
    ],
    outcomesLabel: "Typical outcomes",
    examples: [
      "UAE DTC beauty brand at AED 850K/month ad spend — AI layer rebalanced budget hourly across Meta + TikTok + Google. Blended ROAS lifted from 4.2x to 6.1x in 90 days; total revenue rose AED 1.7M/month at the same spend.",
      "GCC modest-fashion ecom — TikTok creative-fatigue detector paused tired ads 6 days earlier than the team would have caught manually. TikTok ROAS held at 5.4x while scaling spend 5x in one quarter.",
      "Dubai-based education platform — cross-channel reallocation moved AED 240K/quarter from underperforming YouTube TrueView into proven Meta + Google portfolios. Enrolment cost per student dropped 38%.",
      "Premium F&B delivery during Ramadan — bid optimisation reduced cost-per-click on Google by 38% during peak iftar-hour competition. Order volume in the launch window exceeded forecast by 64%.",
    ],
  },
];

export default AI_SOLUTIONS;
